import type { ProvisioningJobView, ProvisioningStep } from "@shared/types/instancesAdmin";

/**
 * How the runner's step names read on screen (canvas 12). #929: in plain words (storage, tables),
 * never schema, migration or binding.
 *
 * The runner owns the names (`runner/steps/provisionInstance.ts` exports
 * `PROVISION_STEP_NAMES`) and they are stable, but they are identifiers, not
 * sentences. Steps are written as ACTIONS — "Create the tenant's storage", not
 * "Database creation" — because a reader watching a list of them is watching
 * something happen, and a noun phrase makes a running step and a finished one
 * read the same.
 *
 * A name with no entry here falls back to its own words rather than to a blank
 * row: the runner may add a step before this map does, and a step that renders
 * as nothing is indistinguishable from a step that did not run.
 */
const STEP_LABELS: Record<string, string> = {
  // ADR 0005: an instance job's steps.
  ensure_tenant_db: "Prepare the tenant's storage",
  create_schema: "Create the instance's storage",
  grant_schema: "Grant the application roles",
  migrate_instance: "Set up the instance's tables",
  // ADR 0010, #766: a per-tenant instance's wait, after its schema and before its binding.
  wait_tenant_resources: "Wait for the tenant's setup",
  // Jobs from before S8 (one database per instance) keep their labels in history.
  create_database: "Create the tenant's storage",
  grant_roles: "Grant the application roles",
  migrate_tenant: "Set up the tenant's tables",
  write_binding: "Record the destination",
  activate: "Activate the instance",
};

export function stepLabel(name: string): string {
  const known = STEP_LABELS[name];
  if (known) return known;
  const words = name.replace(/[_-]+/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Steps that will not change again. */
const FINISHED: ReadonlySet<ProvisioningStep["status"]> = new Set([
  "succeeded",
  "failed",
  "skipped",
]);

export interface ProvisioningProgress {
  completed: number;
  total: number;
  /** The index of the step currently running, or -1 when none is. */
  activeIndex: number;
  /** 0 to 100, for the summary bar. */
  percent: number;
  /** The first failed step, which is where a retry would resume. */
  failedStep: ProvisioningStep | null;
}

export function provisioningProgress(
  job: { steps: ProvisioningStep[] } | undefined,
): ProvisioningProgress {
  const steps = job?.steps ?? [];
  const completed = steps.filter((step) => FINISHED.has(step.status)).length;
  const activeIndex = steps.findIndex((step) => step.status === "running");
  const failedStep = steps.find((step) => step.status === "failed") ?? null;
  return {
    completed,
    total: steps.length,
    activeIndex,
    percent: steps.length === 0 ? 0 : Math.round((completed / steps.length) * 100),
    failedStep,
  };
}

/**
 * "Provisioning 3/5" for the grid cell.
 *
 * The count is in the status itself so the grid alone says how far along a row
 * is; opening the job panel adds the detail, not the fact that there is one.
 */
export function provisioningSummary(job: ProvisioningJobView | undefined): string {
  if (!job) return "Provisioning";
  const { completed, total } = provisioningProgress(job);
  if (total === 0) return job.status === "queued" ? "Queued" : "Provisioning";
  return `Provisioning ${completed}/${total}`;
}
